import { useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";
import OrderList from "../components/OrderList";
import API_URL from "../config/apiConfig";
import { FoodServiceContext } from "../context/FoodServiceContext";
import {
  ORDER_STATUS_TABS,
  countOrdersByStatus,
  filterOrdersByStatus,
  searchOrders,
} from "../services/orderStatusModel";

import { formatPersianNumber as persianNumber } from "../utils/persianNumber";

export default function OrderPage() {
  const { orders, ordersLoading, ordersError, setOrders } = useContext(FoodServiceContext);
  // Kept in the URL (`?status=`) rather than local state, so the sidebar's
  // "سفارش‌ها" sub-links land on the matching tab instead of always "همه".
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStatus = searchParams.get("status");
  const activeStatus = ORDER_STATUS_TABS.some((tab) => tab.value === requestedStatus) ? requestedStatus : "all";
  const setActiveStatus = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("status");
    else next.set("status", value);
    setSearchParams(next, { replace: true });
  };
  const [searchTerm, setSearchTerm] = useState("");

  const statusCounts = useMemo(() => countOrdersByStatus(orders), [orders]);
  const visibleOrders = useMemo(
    () => searchOrders(filterOrdersByStatus(orders, activeStatus), searchTerm),
    [orders, activeStatus, searchTerm]
  );

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${orderId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      // The server also emits `updateOrder` over the shared socket connection
      // (see FoodServiceContext), but updating here immediately avoids
      // waiting on that round-trip for the action that just happened.
      const { order } = await res.json();
      setOrders((current) => current.map((item) => (item._id === order._id ? order : item)));
    } catch {
      toast.error("تغییر وضعیت سفارش ناموفق بود.");
    }
  };

  if (ordersLoading) return <p className="p-4">در حال بارگذاری سفارش‌ها...</p>;
  if (ordersError) return <p className="p-4 text-red-600">{ordersError}</p>;

  return (
    <main className="mx-auto w-full max-w-7xl p-4 sm:p-6 lg:p-8" dir="rtl">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-accent">سفارش‌ها</h1>
          <p className="mt-1 text-sm text-gray-500">مدیریت و پیگیری سفارش‌های رستوران</p>
        </div>
        <label className="relative block">
          <span className="sr-only">جست‌وجوی سفارش</span>
          <FiSearch className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="شماره میز یا فاکتور"
            className="min-h-11 w-56 rounded-xl border border-gray-200 bg-white py-2 pl-3 pr-9 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </label>
      </header>

      <nav className="mt-5 flex gap-2 overflow-x-auto pb-1" aria-label="فیلتر وضعیت سفارش">
        {ORDER_STATUS_TABS.map((tab) => {
          const count = tab.value === "all" ? orders.length : statusCounts[tab.value] || 0;
          const active = activeStatus === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveStatus(tab.value)}
              aria-pressed={active}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                active ? "bg-accent text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              style={{ border: active ? "none" : "1px solid #e5e7eb" }}
            >
              {tab.label}
              <span className={active ? "text-white/80" : "text-gray-400"}>({persianNumber(count)})</span>
            </button>
          );
        })}
      </nav>

      <div className="mt-5">
        <OrderList orders={visibleOrders} onStatusChange={handleStatusChange} />
      </div>
    </main>
  );
}
